import { getMerchantById, MerchantStatus } from '@nemuru-eng/js-api-client';
import { generatePath, useNavigate } from 'react-router-dom';

import { MerchantDataGridFields } from '@/components/modules/MerchantsList/components/MerchantDataGridFields/MerchantDataGridFields';
import { ALLOWED_MERCHANT_STATUSES_FOR_MULTI_SELECT_FILTER } from '@/components/modules/MerchantsList/constants/constants';
import { useMerchantsList } from '@/components/modules/MerchantsList/hooks/useMerchantsList';
import { MerchantStateTag } from '@/components/shared/components/MerchantStateTag/MerchantStateTag';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { MultiFilteredList } from '@/components/shared/components/MultiFilteredList';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { DYNAMIC_PAGE_PATHS } from '@/components/views/DynamicPageView/config/shared/pathRoutes';
import { CreateMerchantDialog } from '@/components/widgets/MerchantListActions/components/CreateMerchantDialog';
import { ModuleCorePermissionProps } from '@/config/types';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCache } from '@/hooks/requests/useCache';
import { withPermissionsModule } from '@/utils/withPermissionsModule';

interface MerchantsListProps extends ModuleCorePermissionProps {
  showInternalData?: boolean;
}

function MerchantsList({ canEdit = false, showInternalData = false }: MerchantsListProps) {
  const {
    state: { lastViewedMerchantId },
    actions: { updateOperationsContext },
  } = useOperationsContext();
  const { fetchMerchantList } = useMerchantsList();
  const navigate = useNavigate();

  const merchant = lastViewedMerchantId
    ? useCache(getMerchantById, { merchantId: lastViewedMerchantId }).data
    : undefined;

  const MERCHANT_STATUSES_ITEMS = ALLOWED_MERCHANT_STATUSES_FOR_MULTI_SELECT_FILTER;

  const tableColumns = MerchantDataGridFields({ showInternalData });

  const { opened, setOpened, toggleDialog } = useDialog();

  const MerchantListActionOptions: ActionsOption[] | undefined = canEdit
    ? [
        {
          display: <Text tId={'CREATE_MERCHANT'} />,
          onClick: () => toggleDialog(),
          iconName: 'add',
        },
      ]
    : undefined;

  return (
    <>
      <SubSectionWrapper specificActionsOptionsArray={MerchantListActionOptions}>
        <MultiFilteredList
          fetchList={fetchMerchantList}
          dataGridFields={tableColumns}
          onRowClick={(selectedRow) => {
            updateOperationsContext({ lastViewedMerchantId: selectedRow.row.id });
            navigate(generatePath(DYNAMIC_PAGE_PATHS.MERCHANT, { merchantId: selectedRow.row.id }));
          }}
          getRowClassName={(params) => {
            if (params.row.id === merchant?.id) return 'last-viewed-application';
            else return '';
          }}
          disableSelectionOnClick
          multiselectProps={{
            field: 'status',
            label: <Text tId={'MERCHANT_STATUSES'} />,
            defaultLabel: <Text tId={'DEFAULT_STATUSES'} />,
            items: MERCHANT_STATUSES_ITEMS,
            checkBoxRender: (value) => <MerchantStateTag status={value} />,
            filtersValueHandler: undefined,
            defaultItems: [
              MerchantStatus.STATUS_DOCUMENTS_COMPLETED,
              MerchantStatus.STATUS_PENDING_DOCUMENTS,
              MerchantStatus.STATUS_PRE_APPROVED,
            ],
          }}
        />
      </SubSectionWrapper>
      <CreateMerchantDialog opened={opened} setOpened={setOpened} toggleDialog={toggleDialog} />
    </>
  );
}

export const MerchantsListModule = withPermissionsModule(MerchantsList);
